import React from 'react';
import { Button, Card, CardContent, LinearProgress } from "@mui/material";
import { ArrowDownCircle, ArrowUpCircle, PiggyBank, PlusCircle, Target, Wallet } from "lucide-react";

const isSameMonth = (dateStr, refDate = new Date()) => {
    const d = new Date(dateStr);
    return d.getFullYear() === refDate.getFullYear() && d.getMonth() === refDate.getMonth();
};

const money = (value) => `${Math.round(value).toLocaleString()} ₽`;

const HomePage = ({
    transactions,
    categories,
    setDialogs
}) => {
    const monthIncome = transactions.income
        .filter(t => isSameMonth(t.date))
        .reduce((sum, t) => sum + t.amount, 0);

    const monthExpenses = transactions.expenses
        .filter(t => isSameMonth(t.date))
        .reduce((sum, t) => sum + t.amount, 0);

    const balance = monthIncome - monthExpenses;
    const budgetLimit = categories.expenses.reduce((sum, category) => sum + Number(category.limit || 0), 0);
    const budgetProgress = budgetLimit > 0 ? Math.min((monthExpenses / budgetLimit) * 100, 100) : 0;

    const metrics = [
        {
            label: 'Баланс месяца',
            value: money(balance),
            tone: balance >= 0 ? 'text-teal-300' : 'text-rose-300',
            icon: Wallet
        },
        {
            label: 'Доходы',
            value: money(monthIncome),
            tone: 'text-emerald-300',
            icon: ArrowUpCircle
        },
        {
            label: 'Расходы',
            value: money(monthExpenses),
            tone: 'text-rose-300',
            icon: ArrowDownCircle
        }
    ];

    return (
        <div className="max-w-6xl mx-auto space-y-6 md:space-y-8">
            <section className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                <div>
                    <p className="text-xs uppercase tracking-[0.22em] text-teal-300/80">Месячный обзор</p>
                    <h2 className="panel-title mt-2 text-3xl md:text-4xl font-bold">Финансы под контролем</h2>
                </div>

                <div className="action-grid md:min-w-[420px]">
                    <Button
                        variant="contained"
                        startIcon={<PlusCircle />}
                        onClick={() => setDialogs(prev => ({ ...prev, expense: true }))}
                        sx={{ minHeight: 46 }}
                    >
                        Расход
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Wallet />}
                        onClick={() => setDialogs(prev => ({ ...prev, income: true }))}
                        sx={{ minHeight: 46, backgroundColor: '#16a34a' }}
                    >
                        Доход
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<Target />}
                        onClick={() => setDialogs(prev => ({ ...prev, goal: true }))}
                        sx={{ minHeight: 46, backgroundColor: '#f59e0b', color: '#111827' }}
                    >
                        Цель
                    </Button>
                </div>
            </section>

            <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {metrics.map(({ label, value, tone, icon: Icon }) => (
                    <Card key={label} elevation={0}>
                        <CardContent>
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="muted-label text-sm">{label}</p>
                                    <p className={`amount-text mt-2 text-3xl font-bold ${tone}`}>{value}</p>
                                </div>
                                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-white/8 text-slate-100">
                                    <Icon size={24} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </section>

            <Card elevation={0}>
                <CardContent>
                    <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                        <div>
                            <h3 className="text-xl font-semibold text-white">Лимиты категорий</h3>
                            <p className="muted-label text-sm">Потрачено {money(monthExpenses)} из {money(budgetLimit)}</p>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-slate-300">
                            <PiggyBank size={18} />
                            <span>{budgetProgress.toFixed(0)}% бюджета</span>
                        </div>
                    </div>
                    <LinearProgress
                        variant="determinate"
                        value={budgetProgress}
                        className="mt-5"
                        sx={{
                            height: 10,
                            borderRadius: 8,
                            backgroundColor: 'rgba(148, 163, 184, 0.18)',
                            '& .MuiLinearProgress-bar': {
                                background: budgetProgress > 90
                                    ? 'linear-gradient(90deg, #fb7185, #f97316)'
                                    : 'linear-gradient(90deg, #2dd4bf, #f59e0b)'
                            }
                        }}
                    />
                </CardContent>
            </Card>

            <section>
                <div className="mb-4 flex items-center justify-between">
                    <h3 className="text-xl font-semibold text-white">Категории расходов</h3>
                    <span className="text-sm text-slate-400">{categories.expenses.length}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                    {categories.expenses.map(category => {
                        const spent = transactions.expenses
                            .filter(t => isSameMonth(t.date) && String(t.categoryId) === String(category.id))
                            .reduce((sum, t) => sum + t.amount, 0);
                        const percentage = category.limit > 0 ? (spent / category.limit) * 100 : 0;

                        return (
                            <Card key={category.id} elevation={0}>
                                <CardContent>
                                    <div className="mb-3 flex items-start justify-between gap-3">
                                        <div className="min-w-0">
                                            <h4 className="truncate text-lg font-semibold text-white">{category.name}</h4>
                                            <p className="muted-label text-sm">{money(spent)} из {money(category.limit)}</p>
                                        </div>
                                        <span className={`amount-text text-sm font-bold ${percentage > 90 ? 'text-rose-300' : 'text-teal-300'}`}>
                                            {Math.min(percentage, 999).toFixed(0)}%
                                        </span>
                                    </div>
                                    <LinearProgress
                                        variant="determinate"
                                        value={Math.min(percentage, 100)}
                                        sx={{
                                            height: 8,
                                            borderRadius: 8,
                                            backgroundColor: 'rgba(148, 163, 184, 0.18)',
                                            '& .MuiLinearProgress-bar': {
                                                backgroundColor: percentage > 90 ? '#fb7185' : '#2dd4bf'
                                            }
                                        }}
                                    />
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            </section>
        </div>
    );
};

export default HomePage;
